/**
 * Maps a marketing-friendly plan slug (`?plan=pro`) onto a document in the
 * Firestore `tiers` collection.
 *
 * Tiers exist only as Firestore documents — nothing in this repo defines them —
 * so the mapping has to live somewhere. Keeping it in this one module means the
 * pages that consume it never learn about Firestore document IDs.
 *
 * Resolution is deliberately ordered from most explicit to most forgiving, so a
 * link keeps working even if the tiers are renamed or this table is never filled
 * in. See `resolveTierId`.
 */
export var TIER_SLUG_RULES = [
    { slug: 'free', tierIds: [], nameIncludes: ['free'] },
    { slug: 'plus', tierIds: [], nameIncludes: ['plus'] },
    { slug: 'pro', tierIds: [], nameIncludes: ['pro'] },
];
/** Display labels for the sign-up page's plan chip. */
export var PLAN_LABELS = {
    free: 'Free',
    plus: 'Plus',
    pro: 'Pro',
};
/** A slug must survive being put back into a URL, so keep the charset tight. */
var SLUG_PATTERN = /^[a-z0-9_-]{1,64}$/;
/**
 * Normalises a raw query-parameter value. Returns null for anything that isn't a
 * plausible slug, so junk is never reflected back into a URL or a Firestore
 * lookup.
 */
export var normalizePlanSlug = function (raw) {
    if (typeof raw !== 'string') {
        return null;
    }
    var slug = raw.trim().toLowerCase();
    return SLUG_PATTERN.test(slug) ? slug : null;
};
/**
 * Resolves a slug to a `tiers` document ID against the tiers already loaded from
 * Firestore, or null when nothing matches.
 *
 * Order:
 *   1. the slug IS a tier document ID — lets a link use a raw ID before any of
 *      the rules below are configured;
 *   2. an explicit `tierIds` entry;
 *   3. a `nameIncludes` substring match on the tier's name;
 *   4. give up.
 *
 * Returning null is not an error: callers fall back to the normal plan grid. A
 * bad marketing link must never block someone from choosing a plan.
 */
export var resolveTierId = function (slug, tiers) {
    if (!slug || !tiers || tiers.length === 0) {
        return null;
    }
    var byId = tiers.find(function (t) { return t.id === slug; });
    if (byId) {
        return byId.id;
    }
    var rule = TIER_SLUG_RULES.find(function (r) { return r.slug === slug; });
    if (rule) {
        var explicit = tiers.find(function (t) { return (rule.tierIds || []).indexOf(t.id) > -1; });
        if (explicit) {
            return explicit.id;
        }
        var needles_1 = rule.nameIncludes || [];
        var byName = tiers.find(function (t) {
            return typeof t.name === 'string' &&
                needles_1.some(function (n) { return t.name.toLowerCase().indexOf(n) > -1; });
        });
        if (byName) {
            return byName.id;
        }
    }
    if (process.env.NODE_ENV !== 'production') {
        // Surfacing the loaded names makes a mismatch self-diagnosing: read the
        // real tier names off the running app and add a rule above.
        // eslint-disable-next-line no-console
        console.warn('[planSlugs] no tier matched "' + slug + '". Loaded tiers: ' +
            tiers.map(function (t) { return '"' + t.name + '" (' + t.id + ')'; }).join(', '));
    }
    return null;
};
